import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { ContentType, ExternalResource, ExternalResourceRequest, ResourceType } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { accession, formatDate } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";
import {
  isoToLocalInput,
  localInputToIso,
  parseTags,
  tagsToInput,
  toNullableString,
} from "./formUtils";

const TYPE_OPTIONS = [
  { value: "LINK", label: "Link" },
  { value: "DOCUMENT", label: "Document" },
  { value: "ARTICLE", label: "Article" },
];

const FIELDS: FieldDef[] = [
  { name: "title", label: "Title", type: "text", required: true },
  { name: "description", label: "Description", type: "textarea" },
  { name: "url", label: "URL", type: "text", required: true },
  { name: "resourceType", label: "Type", type: "select", options: TYPE_OPTIONS },
  { name: "source", label: "Source", type: "text", help: "e.g. Dive Magazine, Surfline" },
  { name: "publishedAt", label: "Published at", type: "datetime" },
  { name: "tags", label: "Tags", type: "tags", help: "Comma-separated, e.g. scuba, egypt" },
];

function toForm(r?: ExternalResource): FormValues {
  return {
    title: r?.title ?? "",
    description: r?.description ?? "",
    url: r?.url ?? "",
    resourceType: r?.resourceType ?? "LINK",
    source: r?.source ?? "",
    publishedAt: isoToLocalInput(r?.publishedAt),
    tags: tagsToInput(r?.tags),
  };
}

function toRequest(v: FormValues): ExternalResourceRequest {
  return {
    title: String(v.title ?? "").trim(),
    description: toNullableString(v.description),
    url: String(v.url ?? "").trim(),
    resourceType: (String(v.resourceType ?? "LINK") as ResourceType) || "LINK",
    source: toNullableString(v.source),
    publishedAt: localInputToIso(v.publishedAt as string),
    tags: parseTags(v.tags),
  };
}

export function AdminDocuments() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listResources(), [refreshToken, reload]);
  const rows = data?.items ?? [];
  const [editing, setEditing] = useState<ExternalResource | null>(null);
  const [creating, setCreating] = useState(false);
  const refetch = () => setReload((n) => n + 1);

  const columns: Column<ExternalResource>[] = [
    {
      key: "code",
      header: "Code",
      render: (r) => <span className="mono">{accession(r.resourceType as ContentType, r.id)}</span>,
      sortValue: (r) => r.id,
    },
    {
      key: "title",
      header: "Title",
      className: "cell-title",
      render: (r) => r.title,
      sortValue: (r) => r.title,
    },
    {
      key: "type",
      header: "Type",
      render: (r) => <span className="pill">{r.resourceType}</span>,
      sortValue: (r) => r.resourceType,
    },
    {
      key: "source",
      header: "Source",
      render: (r) => <span className="cell-muted">{r.source || "—"}</span>,
      sortValue: (r) => r.source ?? "",
    },
    {
      key: "publishedAt",
      header: "Published",
      render: (r) => <span className="mono">{formatDate(r.publishedAt) || "—"}</span>,
      sortValue: (r) => r.publishedAt ?? "",
    },
  ];

  return (
    <>
      <AdminTable
        rows={rows}
        getId={(r) => r.id}
        columns={columns}
        searchText={(r) => `${r.title} ${r.tags.join(" ")} ${r.source ?? ""} ${r.description ?? ""}`}
        searchPlaceholder="Filter references…"
        typeFilter={{
          label: "Type",
          value: (r) => r.resourceType,
          options: TYPE_OPTIONS,
        }}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No references yet."
        createLabel="+ New reference"
        onCreate={() => setCreating(true)}
        onEdit={(r) => setEditing(r)}
        onDelete={async (r) => {
          await adminApi.deleteResource(r.id);
          refetch();
        }}
        bulkActions={[
          {
            label: "Delete selected",
            danger: true,
            confirm: "Delete all selected references?",
            run: async (sel) => {
              for (const r of sel) await adminApi.deleteResource(r.id);
              refetch();
            },
          },
        ]}
      />
      {creating && (
        <AdminForm
          title="New reference"
          fields={FIELDS}
          initial={toForm()}
          submitLabel="Create"
          onCancel={() => setCreating(false)}
          onSubmit={async (v) => {
            await adminApi.createResource(toRequest(v));
            setCreating(false);
            refetch();
          }}
        />
      )}
      {editing && (
        <AdminForm
          title={`Edit ${accession(editing.resourceType as ContentType, editing.id)}`}
          fields={FIELDS}
          initial={toForm(editing)}
          onCancel={() => setEditing(null)}
          onSubmit={async (v) => {
            await adminApi.updateResource(editing.id, toRequest(v));
            setEditing(null);
            refetch();
          }}
        />
      )}
    </>
  );
}
